import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ExcelService } from '@app/@shared/services/print/excel.service';
import jsPDF from 'jspdf';
import autoTable, { Table } from 'jspdf-autotable';
import moment from 'moment';
import { ControlPanelStore } from '../control-panel-service/control-panel.store';
import { AgentGroupDTO, AgentStatsExport, AgentStatsReportDTO } from '../models/control-panel.model';
import { TransformNumberPipe } from '@app/@shared/pipes/transform-number.pipe';
import FileSaver from 'file-saver';
import { TranslateService } from '@ngx-translate/core';
import { DATE_FORMAT_MMMM_YYYY, LOCALE_CA, LOCALE_ES } from '@app/@core/constants/common.constants';

@Component({
  selector: 'app-agent-statistics',
  templateUrl: './agent-statistics.component.html',
  styleUrls: ['./agent-statistics.component.scss'],
})
export class AgentStatisticsComponent implements OnInit {
  data: AgentStatsReportDTO;

  numUsers: number;

  formData: FormGroup; //Variable needed to store form in case of printing or exporting to excel

  private division: string;
  private product: string;
  private typeArticle: string;

  //Variable needed for table order of columns
  displayedColumns: string[] = [
    'numOperation',
    'investment',
    'TIR',
    'PI',
    'totalCommissionAgent',
    'percentageCommission',
    'totalCommission',
  ];

  private head: string[] = [
    //Used in excel and printing
    'agentStatistics.agent',
    '',
    'agentStatistics.numOperation',
    'agentStatistics.investment',
    'agentStatistics.TIR',
    'agentStatistics.PI',
    'agentStatistics.totalCommissionAgent',
    'agentStatistics.percentageCommission',
    'agentStatistics.totalCommission',
  ];

  totals: any[] = [];

  constructor(
    private excelService: ExcelService,
    private controlPanelStore: ControlPanelStore,
    private transformNumberPipe: TransformNumberPipe,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {}

  loadDataTable(data: any): void {
    this.totals = [];
    this.data = undefined;
    this.division = data?.division;
    this.product = data?.product ? data?.product : this.translateService.instant('select.option.all');
    this.typeArticle = data?.typeArticle ? data?.typeArticle : this.translateService.instant('select.option.all');
    this.formData = data?.form;
    this.controlPanelStore.getAgentStatsReport(this.formData).then(
      (data) => {
        this.data = data;
        this.totals.push({
          totalInversion: data.totalInversion,
          totalNumOperations: data.totalNumOperations,
          totalTirAverage: data.totalTirAverage,
          totalTermsAverage: data.totalTermsAverage,
          totalAgentCommission: data.totalAgentCommission,
          totalPercentCommission: data.totalPercentCommission,
          totalCommissions: data.totalCommissions,
        });
      },
      (error) => console.error(error)
    );
  }

  createPdf() {
    new Promise((reject) => {
      try {
        let doc = new jsPDF({
          orientation: 'portrait',
          format: [209.804, 296.926],
        });

        const filename =
          this.translateService.instant('excel.agentStatistics.filename') + moment().locale('es').format('DDMMYYYY');

        doc.setProperties({
          title: filename,
        });

        /* BODY */
        this.data?.items.forEach((group) => {
          let dataPdf: any[] = [];
          group.items.forEach((agent) => {
            const dataPDFOrder: AgentStatsExport = this.generateAgentStatsExport(
              agent.establishmendId,
              agent.userDesc,
              agent.numOperations,
              agent.inversion,
              agent.tirAverage,
              agent.termsAverage,
              agent.agentCommission,
              agent.percentCommission,
              agent.totalCommissions,
              true
            );
            dataPdf.push(dataPDFOrder);
          });

          //Total
          const groupTotal = this.generateAgentStatsExport(
            '',
            this.translateService.instant('agentStatistics.total.office') + ': ',
            group.totalNumOperations,
            group.totalInversion,
            group.totalTirAverage,
            group.totalTermsAverage,
            group.totalAgentCommission,
            group.totalPercentCommission,
            group.totalCommissions,
            true
          );
          dataPdf.push(groupTotal);

          doc = this.createPdfTable(doc, dataPdf, group);
        });

        //Total de totales
        const totalPDF = [];
        const agentTotal: AgentStatsExport = this.generateAgentStatsExport(
          '',
          this.translateService.instant('agentStatistics.totalAgent') + ':',
          this.data.totalNumOperations,
          this.data.totalInversion,
          this.data.totalTirAverage,
          this.data.totalTermsAverage,
          this.data.totalAgentCommission,
          this.data.totalPercentCommission,
          this.data.totalCommissions,
          true
        );
        totalPDF.push(agentTotal);

        this.createPdfTable(doc, totalPDF);

        var blob = doc.output('blob');
        FileSaver.saveAs(blob, filename);
        window.open(URL.createObjectURL(blob));
      } catch (error) {
        console.error(error);
        reject(error);
      }
    });
  }

  createPdfTable(doc: jsPDF, dataPdf: any, group?: AgentGroupDTO): jsPDF {
    const lastAutoTable: Table = (doc as any).lastAutoTable;
    const maxTableYCoordinate: number = 266;
    //First time
    doc.setFontSize(10);
    if (lastAutoTable && lastAutoTable?.finalY < maxTableYCoordinate && group) {
      doc.text('Oficina: ' + group.officeId + ' ' + group.name, 14, lastAutoTable.finalY + 8);
    }

    const tableYCoordinate: number = 70;
    let heightLastRow: number = 0;

    autoTable(doc, {
      startY:
        lastAutoTable.finalY && lastAutoTable.finalY > tableYCoordinate ? lastAutoTable.finalY + 10 : tableYCoordinate,
      head: [this.head.map((header) => (header ? this.translateService.instant(header) : header))],
      headStyles: { fillColor: '#969696' },
      rowPageBreak: 'avoid',
      showHead: 'everyPage',
      margin: { top: tableYCoordinate },
      body: dataPdf.map(Object.values),
      styles: { fontSize: 8 },
      didDrawCell: (data) => {
        if (data.row.index === dataPdf.length - 1) {
          heightLastRow = data.row.height;
        }
      },
      didDrawPage: (data) => {
        /* HEADER */
        if (
          !lastAutoTable ||
          (lastAutoTable && data.pageNumber !== 1) ||
          (lastAutoTable && data.pageNumber === 1 && lastAutoTable.finalY > maxTableYCoordinate)
        ) {
          //TITLE
          const headerXCoordinateES: number = 80;
          const headerXCoordinateCA: number = 65;
          const headerYCoordinate: number = 20;
          doc.setFontSize(24);
          doc.setTextColor('1E90FF');

          let text = this.translateService.instant('excel.dashboard.title');
          if (text.length <= 27) doc.text(text, headerXCoordinateES, headerYCoordinate);
          else doc.text(text, headerXCoordinateCA, headerYCoordinate);

          var img = new Image();
          img.src = 'assets/img/logo.PNG';
          doc.addImage(img, 'png', 3, 10, 58, 20);

          //First row
          doc.setTextColor('#000000');
          doc.setFontSize(12);

          const firstRowYCoordinate: number = 35;
          const titleXCoordinate: number = 75;
          doc.text(this.translateService.instant('agentStatistics.subtitle'), titleXCoordinate, firstRowYCoordinate);
          doc.setFontSize(10);

          const divisionXCoordinate: number = 14;
          doc.text(
            this.translateService.instant('excel.division') + this.division,
            divisionXCoordinate,
            firstRowYCoordinate
          );

          const thirdColumnXCoordinate: number = 150;
          doc.text(
            this.translateService.instant('excel.date') + moment().locale('es').format('DD-MM-YYYY HH:mm:ss'),
            thirdColumnXCoordinate,
            firstRowYCoordinate - 3
          );
          doc.text(
            this.translateService.instant('excel.pag') + doc.getNumberOfPages(),
            thirdColumnXCoordinate,
            firstRowYCoordinate + 3
          );

          //Second row
          const secondRowYCoordinate: number = 45;
          const secondRowXCoordinate: number = 70;
          const periodText: string = this.translateService.instant('excel.period');

          doc.text(periodText, secondRowXCoordinate, secondRowYCoordinate);
          doc.text(
            this.getInitialDate(DATE_FORMAT_MMMM_YYYY) + ' a ' + this.getFinalDate(DATE_FORMAT_MMMM_YYYY),
            doc.getTextWidth(periodText) + secondRowXCoordinate,
            secondRowYCoordinate
          );

          this.updateHeadersByLanguage();
          doc.text(
            this.translateService.instant('excel.product') + this.product,
            secondRowXCoordinate,
            secondRowYCoordinate + 6
          );
          doc.text(
            this.translateService.instant('excel.typeOfGood') + this.typeArticle,
            secondRowXCoordinate,
            secondRowYCoordinate + 12
          );

          if (lastAutoTable) {
            if (data.pageNumber !== 1 || lastAutoTable.finalY > maxTableYCoordinate) {
              doc.text(this.translateService.instant('excel.office') + group?.name, 14, tableYCoordinate - 2);
            }
          } else {
            doc.text(this.translateService.instant('excel.office') + group?.name, 14, tableYCoordinate - 2); //First case always
          }
        }
      },
    });

    const currentAutoTable: Table = (doc as any).lastAutoTable;
    doc.line(14, currentAutoTable.finalY - heightLastRow, 196, currentAutoTable.finalY - heightLastRow, 'F');

    return doc;
  }

  generateAgentStatsExport(
    establishmentId: string,
    name: string,
    numOperation: number,
    inversion: number,
    tirAverage: number,
    termsAverage: number,
    agentCommission: number,
    percentCommission: number,
    totalCommissions: number,
    isPDF: boolean
  ): AgentStatsExport {
    return {
      establishmendId: establishmentId,
      userDesc: name,
      numOperations: establishmentId ? numOperation.toString() : this.transformNumberPipe.transform(numOperation),
      inversion: isPDF
        ? this.transformNumberPipe.transform(inversion)
        : inversion?.toFixed(2).toString().replace('.', ','),
      tirAverage: isPDF
        ? this.transformNumberPipe.transform(tirAverage)
        : tirAverage?.toFixed(2).toString().replace('.', ','),
      termsAverage: isPDF
        ? this.transformNumberPipe.transform(termsAverage)
        : termsAverage?.toFixed(2).toString().replace('.', ','),
      agentCommission: isPDF
        ? this.transformNumberPipe.transform(agentCommission)
        : agentCommission?.toFixed(2).toString().replace('.', ','),
      percentCommission: isPDF
        ? this.transformNumberPipe.transform(percentCommission)
        : percentCommission?.toFixed(2).toString().replace('.', ','),
      totalCommissions: isPDF
        ? this.transformNumberPipe.transform(totalCommissions)
        : totalCommissions?.toFixed(2).toString().replace('.', ','),
    };
  }

  createExcel() {
    const dataForExcel: any[] = [];
    const filename =
      this.translateService.instant('excel.agentStatistics.filename') + moment().locale('es').format('DDMMYYYY');

    /* BODY */
    this.data?.items.forEach((group) => {
      const dataGroup: any[] = [];

      const IdNameGroup = {
        officeID: group.officeId,
        name: group.name,
      };

      dataGroup.push(Object.values(IdNameGroup));

      group.items.forEach((agent) => {
        const agentStats = this.generateAgentStatsExport(
          agent.establishmendId,
          agent.userDesc,
          agent.numOperations,
          agent.inversion,
          agent.tirAverage,
          agent.termsAverage,
          agent.agentCommission,
          agent.percentCommission,
          agent.totalCommissions,
          false
        );

        dataGroup.push(Object.values(agentStats));
      });
      //Total
      const groupTotal = this.generateAgentStatsExport(
        '',
        this.translateService.instant('agentStatistics.total.office') + ': ',
        group.totalNumOperations,
        group.totalInversion,
        group.totalTirAverage,
        group.totalTermsAverage,
        group.totalAgentCommission,
        group.totalPercentCommission,
        group.totalCommissions,
        false
      );
      dataGroup.push(Object.values(groupTotal));

      dataForExcel.push(dataGroup);
    });

    const dataTotal = [];
    const totalAgent = this.generateAgentStatsExport(
      '',
      this.translateService.instant('agentStatistics.totalAgent') + ': ',
      this.data.totalNumOperations,
      this.data.totalInversion,
      this.data.totalTirAverage,
      this.data.totalTermsAverage,
      this.data.totalAgentCommission,
      this.data.totalPercentCommission,
      this.data.totalCommissions,
      false
    );

    dataTotal.push(Object.values(totalAgent));
    dataForExcel.push(dataTotal);

    const dates: string[] = [];
    dates.push(this.getInitialDate(DATE_FORMAT_MMMM_YYYY));
    dates.push(this.getFinalDate(DATE_FORMAT_MMMM_YYYY));

    this.updateHeadersByLanguage();
    const excelData = {
      fileName: filename,
      headers: this.head.map((header) => (header ? this.translateService.instant(header) : header)),
      data: dataForExcel,
      division: this.division,
      product: this.product,
      typeArticle: this.typeArticle,
      date: dates,
    };
    this.excelService.exportExcelAgentStatistics(excelData);
  }

  private getInitialDate(format: string) {
    return this.formData
      ?.get('initialDate')
      ?.value?.locale(this.translateService.currentLang === LOCALE_ES ? LOCALE_ES : LOCALE_CA)
      .format(format);
  }

  private getFinalDate(format: string) {
    return this.formData
      ?.get('finalDate')
      ?.value?.locale(this.translateService.currentLang === LOCALE_ES ? LOCALE_ES : LOCALE_CA)
      .format(format);
  }

  private updateHeadersByLanguage() {
    if (this.product.toUpperCase() === 'TODOS' || this.product.toUpperCase() === 'TOTS') {
      this.product = this.translateService.instant('select.option.all');
    }

    if (this.typeArticle.toUpperCase() === 'TODOS' || this.typeArticle.toUpperCase() === 'TOTS') {
      this.typeArticle = this.translateService.instant('select.option.all');
    }
  }
}
